import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Typography from '@/components/ui/typography';
import { Card, CardContent } from '@/components/ui/card';
import { TeamMember } from '@/types/api';
import { AppDispatch, RootState } from '@/store/store';
import {
  updateUserProfile,
  resetUpdateUserError,
  resetUpdateUserSuccess,
} from '@/store/teamSlice';
import { useToast } from '@/components/ui/toast';

interface FieldErrors {
  role?: string;
}

interface EditUserModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: TeamMember | null;
  tenantId: string;
  ehrUsernameLabel: string;
}

const roleOptions = [
  { value: 'BILLER', label: 'Biller' },
  { value: 'CODER', label: 'Coder' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'DOCTOR_ASSISTANT', label: 'Assistant' },
];

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  onOpenChange,
  user,
  tenantId,
  ehrUsernameLabel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { updateUserLoading, updateUserError, updateUserSuccess } = useSelector(
    (state: RootState) => state.team,
  );
  const { addToast } = useToast();

  const [formData, setFormData] = useState<TeamMember>({
    uuid: '',
    authUserId: null,
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    lastLogin: null,
    ehrUsername: '',
  });

  const [errors, setErrors] = useState<FieldErrors>({});
  const [hasInitiatedUpdate, setHasInitiatedUpdate] = useState(false);

  // Update form data when user prop changes
  useEffect(() => {
    if (user) {
      setFormData(user);
      setErrors({});
      setHasInitiatedUpdate(false);
      dispatch(resetUpdateUserError());
    }
  }, [user, dispatch]);

  // Close modal on successful update
  useEffect(() => {
    if (
      hasInitiatedUpdate &&
      !updateUserLoading &&
      !updateUserError &&
      updateUserSuccess
    ) {
      addToast({
        message: updateUserSuccess,
        type: 'success',
        duration: 3000,
      });
      onOpenChange(false);
      setHasInitiatedUpdate(false);
      dispatch(resetUpdateUserSuccess());
    }
  }, [
    hasInitiatedUpdate,
    updateUserLoading,
    updateUserError,
    updateUserSuccess,
    onOpenChange,
    addToast,
    dispatch,
  ]);

  // Show error toast when update fails
  useEffect(() => {
    if (hasInitiatedUpdate && !updateUserLoading && updateUserError) {
      addToast({
        message: updateUserError,
        type: 'error',
        duration: 5000,
      });
      setHasInitiatedUpdate(false);
    }
  }, [hasInitiatedUpdate, updateUserLoading, updateUserError, addToast]);

  const updateField = (field: keyof TeamMember, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === 'role') {
      setErrors({ role: value.trim() ? undefined : 'Role is required' });
    }
  };

  const handleSave = () => {
    if (!formData.role.trim()) {
      setErrors({ role: 'Role is required' });
      return;
    }
    const userData = {
      uuid: formData.uuid,
      roleName: formData.role,
      ehrUsername: formData.ehrUsername?.trim() || undefined,
    };
    dispatch(updateUserProfile({ userData, tenantId }));
    setHasInitiatedUpdate(true);
  };

  const handleCancel = () => {
    onOpenChange(false);
    if (user) {
      setFormData(user);
    }
    setErrors({});
    setHasInitiatedUpdate(false);
    dispatch(resetUpdateUserError());
    addToast({
      message: 'Edit cancelled',
      type: 'info',
      duration: 2000,
    });
  };

  const isValid = Boolean(formData.role);

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-left">
            <Typography
              variant="h2"
              weight="semibold"
              className="text-neutral-900"
            >
              Edit team member
            </Typography>
          </DialogTitle>
          <Typography variant="small" className="text-neutral-600 mt-2">
            Update the user's information
          </Typography>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="border border-[#F3F4F6] bg-[#F7F8FA]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <Typography
                  variant="h4"
                  weight="medium"
                  className="text-neutral-900"
                >
                  User Information
                </Typography>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    First Name
                  </Typography>
                  <Input
                    value={formData.firstName}
                    disabled={true}
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </div>

                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    Last Name
                  </Typography>
                  <Input
                    value={formData.lastName}
                    disabled={true}
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    Email
                  </Typography>
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    disabled={true}
                    className="bg-gray-100 cursor-not-allowed"
                  />
                </div>
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    Role
                  </Typography>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => updateField('role', value)}
                  >
                    <SelectTrigger
                      className={errors.role ? 'border-red-500' : ''}
                    >
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          {role.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.role && (
                    <Typography
                      size="12"
                      weight="light"
                      className="text-red-500"
                    >
                      {errors.role}
                    </Typography>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <Typography
                    variant="small"
                    className="text-neutral-700 font-medium"
                  >
                    {ehrUsernameLabel}
                  </Typography>
                  <Input
                    placeholder={`Enter ${ehrUsernameLabel}`}
                    value={formData.ehrUsername ?? ''}
                    onChange={(e) => updateField('ehrUsername', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex gap-3">
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={updateUserLoading || !isValid}
            className="flex-1 sm:flex-none"
          >
            {updateUserLoading ? 'Saving...' : 'Save changes'}
          </Button>

          <Button
            variant="outline"
            onClick={handleCancel}
            className="flex-1 sm:flex-none"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserModal;
